import React from 'react';
import { useTranslation } from '@shared/core';
import { useCreateAccountController } from '@apps/acquisitionFunnels/createWedding/routes/CreateAccount/CreateAccount.controller';
import { ReactComponent as FacebookIcon } from '@assets/icons/facebook.svg';
import { ReactComponent as GoogleIcon } from '@assets/icons/google.svg';
import { PixelImage } from '@apps/acquisitionFunnels/createWedding/components/PixelImage';
import { ButtonV2, Flex, FormControl, SpacingStack, TextV2 } from '@withjoy/joykit';
import { InlineLinkButton } from '@apps/acquisitionFunnels/createWedding/components/InlineLinkButton/InlineLinkButton';
import { InputV2InlineLabel } from '@shared/joykit/packages/core/components/InputV2InlineLabel/InputV2InlineLabel';
import { ShowDevAlert } from '@apps/acquisitionFunnels/createWedding/components/ShowDevAlert';
import { wrapWithCreateAccountHealthProvider } from './CreateAccount.health';
import { CREATE_WEDDING_CONTAINER_MAX_WIDTH } from '@apps/acquisitionFunnels/createWedding/components/CreateWeddingLayout/CreateWeddingLayout';
import { pxToRem } from '@withjoy/joykit/theme';
import { ReactComponent as IconBrandLogoNoColor } from '@assets/icons/icon-brand-logo-no-color.svg';
import { AccountAgreementNotice } from './components/AccountAgreementNotice';
import { FindCouple } from './components/FindCouple';
import { useHeroTextByScenario } from '@apps/acquisitionFunnels/createWedding/hooks/useHeroTextByScenario';

const CreateAccountContent = wrapWithCreateAccountHealthProvider(() => {
  const { tq } = useTranslation('createWedding');
  const { createAccountHeroText } = useHeroTextByScenario();

  const { formik, submissionError, goToSignIn, signInWithFacebook, signInWithGoogle, showSocialSignIn, focusHandlers, getFieldErrorProps } = useCreateAccountController();

  return (
    <>
      <IconBrandLogoNoColor
        aria-hidden="true"
        css={{ width: pxToRem(67), height: pxToRem(42) }}
      />
      <ShowDevAlert />
      <SpacingStack spacing={4}>
        <TextV2
          as="h1"
          typographyVariant={'hed6'}
          fontWeight={600}
          marginTop={5}
          textAlign={'center'}
        >
          {createAccountHeroText}
        </TextV2>
        <Flex
          textAlign="center"
          justifyContent="center"
          typographyVariant="body2"
          color="mono12"
          alignItems="center"
        >
          {tq`Already have an account?`}
          <InlineLinkButton onClick={goToSignIn}>{tq`Sign In`}</InlineLinkButton>
        </Flex>
      </SpacingStack>
      {showSocialSignIn && (
        <SpacingStack spacing={6}>
          <ButtonV2
            onClick={signInWithFacebook}
            variant="outline"
            startIcon={<FacebookIcon />}
          >
            {tq`Sign in with Facebook`}
          </ButtonV2>
          <ButtonV2
            onClick={signInWithGoogle}
            variant="outline"
            startIcon={<GoogleIcon />}
          >
            {tq`Sign in with Google`}
          </ButtonV2>
        </SpacingStack>
      )}
      <Flex
        as="form"
        onSubmit={formik.handleSubmit}
        flexDirection={'column'}
        gap={6}
        marginTop={5}
      >
        <FormControl
          onFocus={focusHandlers.email}
          {...getFieldErrorProps('email')}
        >
          <InputV2InlineLabel
            autoComplete="username"
            type="email"
            label={tq`Email`}
            {...formik.getFieldProps('email')}
            {...getFieldErrorProps('email')}
          />
        </FormControl>
        <FormControl
          onFocus={focusHandlers.password}
          {...getFieldErrorProps('password')}
        >
          <InputV2InlineLabel
            autoComplete="new-password"
            type="password"
            label={tq`Password`}
            {...formik.getFieldProps('password')}
            {...getFieldErrorProps('password')}
          />
        </FormControl>
        {submissionError && (
          <TextV2
            role="alert"
            textAlign="center"
            typographyVariant="body1"
            color="negative6"
          >
            {submissionError}
          </TextV2>
        )}
        <SpacingStack spacing={7}>
          <ButtonV2
            shape="rounded"
            loading={formik.isSubmitting}
            type="submit"
            intent="primary"
            textOverflow={'ellipsis'}
          >
            {tq`Continue`}
          </ButtonV2>
          <AccountAgreementNotice />
          <FindCouple />
          <PixelImage action="signup" />
        </SpacingStack>
      </Flex>
    </>
  );
});

export const CreateAccount = () => {
  return (
    <Flex
      width={'100%'}
      maxWidth={{ _: '100%', sm2: pxToRem(CREATE_WEDDING_CONTAINER_MAX_WIDTH) }}
      flexDirection="column"
      alignItems="center"
      gap={5}
    >
      <CreateAccountContent />
    </Flex>
  );
};

export default CreateAccount;
